import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { EMOTION_SERIES, PLAYHEAD, PLAYHEAD_TOOLTIP } from './resultsMockData'

/** Big synchronized emotion timeline: 7 SVG series, y 0–100%, x 0:00–0:30,
 *  peak markers, a dashed playhead and a tooltip card. */
export default function EmotionTimeline({ height = 220 }: { height?: number }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  const W = 920
  const H = height
  const padL = 36
  const padR = 12
  const padT = 14
  const padB = 26
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  const len = EMOTION_SERIES[0].d.length
  const x = (i: number) => padL + (i / (len - 1)) * innerW
  const y = (v: number) => padT + (1 - v) * innerH
  const buildPath = (d: number[]) => d.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')

  const playX = padL + PLAYHEAD * innerW

  return (
    <Box component="svg" width="100%" viewBox={`0 0 ${W} ${H}`} sx={{ display: 'block' }} preserveAspectRatio="xMidYMid meet">
      {/* y ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map((tk) => (
        <g key={tk}>
          <line x1={padL} y1={y(tk)} x2={W - padR} y2={y(tk)} stroke={c.ink08} strokeWidth="1" />
          <text x={padL - 8} y={y(tk) + 3} textAnchor="end" fontFamily={theme.affexy.fonts.body} fontSize="10" fill={c.ink48}>
            {Math.round(tk * 100)}%
          </text>
        </g>
      ))}
      {/* x labels */}
      {[0, 5, 10, 15, 20, 25, 30].map((tk) => (
        <g key={tk}>
          <line x1={padL + (tk / 30) * innerW} y1={H - padB} x2={padL + (tk / 30) * innerW} y2={H - padB + 4} stroke={c.ink32} strokeWidth="1" />
          <text
            x={padL + (tk / 30) * innerW}
            y={H - padB + 16}
            textAnchor="middle"
            fontFamily={theme.affexy.fonts.body}
            fontSize="10"
            fill={c.ink56}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            0:{String(tk).padStart(2, '0')}
          </text>
        </g>
      ))}

      {/* series — joy + attention bolder, others muted */}
      {EMOTION_SERIES.map((s) => {
        const primary = s.k === 'joy' || s.k === 'attention'
        return (
          <path
            key={s.k}
            d={buildPath(s.d)}
            fill="none"
            stroke={theme.affexy.emotion[s.k]}
            strokeWidth={primary ? 2.4 : 1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={primary ? 1 : 0.75}
          />
        )
      })}

      {/* peak markers */}
      <circle cx={x(4)} cy={y(0.48)} r={5} fill={theme.affexy.emotion.surprise} stroke="#fff" strokeWidth="2" />
      <circle cx={x(25)} cy={y(0.88)} r={5} fill={theme.affexy.emotion.joy} stroke="#fff" strokeWidth="2" />

      {/* playhead */}
      <line x1={playX} y1={padT} x2={playX} y2={H - padB} stroke={c.ink} strokeWidth="1.5" strokeDasharray="3 3" />
      <polygon points={`${playX - 6},${padT - 4} ${playX + 6},${padT - 4} ${playX},${padT + 4}`} fill={c.ink} />

      {/* tooltip */}
      <g>
        <rect x={playX + 8} y={padT - 2} width={148} height={92} rx={10} fill={c.navy} opacity="0.98" />
        <text x={playX + 18} y={padT + 16} fontFamily={theme.affexy.fonts.body} fontSize="10.5" fill="rgba(255,255,255,.5)" letterSpacing=".06em">
          0:23
        </text>
        <g fontFamily={theme.affexy.fonts.body} fontSize="11" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {PLAYHEAD_TOOLTIP.map((row, i) => {
            const ry = padT + 34 + i * 16
            return (
              <g key={row.k}>
                <text x={playX + 18} y={ry} fill={row.muted ? 'rgba(255,255,255,.6)' : '#fff'}>
                  {t(`tooltip.${row.k}`)}
                </text>
                <text
                  x={playX + 138}
                  y={ry}
                  textAnchor="end"
                  fill={row.muted ? 'rgba(255,255,255,.72)' : theme.affexy.emotion[row.k]}
                  fontWeight="700"
                >
                  {row.pct}%
                </text>
              </g>
            )
          })}
        </g>
      </g>
    </Box>
  )
}
